import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { getFileHref } from "@/lib/path-utils";
import { runEvaluatorsOnLog } from "@/services/evaluations.service";
import { Evaluator } from "@/types/app/evaluator";
import { File } from "@/types/app/file";
import { EvaluatorLog, getFileFromLog, Log, parseLogResponse } from "@/types/app/log";
import { ContextMenu } from "@components/library/ContextMenu";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import IconButton from "@components/library/IconButton";
import { PageSectionHeader } from "@components/library/PageSectionHeader";
import { hlToastApiError } from "@components/library/Toast";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "@components/llama/DescriptionList";
import { EvaluationValueWithPopover } from "@components/llama/Evaluations/EvaluationResult";
import { None } from "@components/llama/Tables/Logs/LogCells";
import TypingIndicator from "@components/PromptEditor/Chat/TypingIndicator";
import { InlineMenu } from "@components/SimpleTable/InlineMenu";
import { CogIcon } from "@heroicons/react/outline";
import { Play } from "@phosphor-icons/react";
import React, { useMemo, useState } from "react";
import { EvaluationDetailsActions } from "./EvaluationDetailsActions";
import Tooltip, { ConditionalTooltip } from "@components/library/Tooltip";

interface Props {
  file: File;
  log: Log;
  evaluatorLogs: Log[];
  onFeedbackChange: (evaluatorLog: EvaluatorLog) => void;
  allowRunEvaluations?: boolean;
  onRunEvaluations: (evaluatorLogs: EvaluatorLog[]) => void;
}

export const EvaluationDetails = ({ log, evaluatorLogs, file, onFeedbackChange, onRunEvaluations }: Props) => {
  // We want to show the full set of evaluators:
  // those that are connected to the File
  // and those that have been run on this Log.

  // The monitoring File is the active file/"project" set in the URL and this File has the known attached evaluators.
  // This file might not be exactly the same as the file passed in, because we might be rendering this LogDrawer
  // in a different context like a session etc. Therefore, we only show the monitoring file evaluators (the evaluators
  //  that are enabled for this file) if the two files are the same. This means we can add a run button next to them.
  const { file: monitoringFile, mutate: mutateMonitoringFile } = useMonitoringFile();
  const monitoringFileIsLogFile = monitoringFile?.id === file.id;

  const evaluatorsToShow: Evaluator[] = useMemo(() => {
    const attachedEvaluators =
      monitoringFileIsLogFile && "evaluators" in monitoringFile
        ? monitoringFile.evaluators?.map((e) => e.version)?.filter((e): e is Evaluator => !!e) || []
        : [];

    const runEvaluators = evaluatorLogs
      .map((log) => getFileFromLog(log))
      .filter((file): file is Evaluator => file?.type === "evaluator");

    const allEvaluators = [...attachedEvaluators, ...runEvaluators];

    // Remove duplicates based on evaluator version_id
    return Array.from(new Map(allEvaluators.map((evaluator) => [evaluator.version_id, evaluator])).values());
  }, [monitoringFileIsLogFile, monitoringFile, evaluatorLogs]);

  const { slug } = useActiveOrganization();

  const latestEvaluatorLogPerEvaluator: {
    evaluator: Evaluator;
    evaluatorLog: EvaluatorLog | null;
  }[] = useMemo(() => {
    const latestEvaluatorLogs: {
      evaluator: Evaluator;
      evaluatorLog: EvaluatorLog | null;
    }[] = [];

    const evaluatorLogsByEvaluatorVersionId = new Map<string, EvaluatorLog[]>();

    evaluatorLogs.forEach((evaluatorLog) => {
      const evaluator = getFileFromLog(evaluatorLog);
      if (!evaluator) {
        return;
      }

      const logs = evaluatorLogsByEvaluatorVersionId.get(evaluator.version_id) || [];
      logs.push(evaluatorLog as EvaluatorLog);
      evaluatorLogsByEvaluatorVersionId.set(evaluator.version_id, logs);
    });

    // For each evaluator in evaluatorsToShow, find the latest log or set to null if no log exists
    evaluatorsToShow.forEach((evaluator) => {
      const logs = evaluatorLogsByEvaluatorVersionId.get(evaluator.version_id) || [];
      const latestLog =
        logs.length > 0 ? logs.reduce((acc, log) => (log.created_at > acc.created_at ? log : acc)) : null;
      latestEvaluatorLogs.push({ evaluator, evaluatorLog: latestLog });
    });

    // Sort by evaluator name
    latestEvaluatorLogs.sort((a, b) => a.evaluator.name.localeCompare(b.evaluator.name));

    return latestEvaluatorLogs;
  }, [evaluatorLogs, evaluatorsToShow]);

  const [runningEvaluatorVersionIds, setRunningEvaluatorVersionIds] = useState<string[]>([]);

  const handleRun = async (evaluator: Evaluator, log: Log) => {
    if (!log) return;

    if (runningEvaluatorVersionIds.includes(evaluator.version_id)) {
      return;
    }
    setRunningEvaluatorVersionIds([...runningEvaluatorVersionIds, evaluator.version_id]);
    try {
      const runEvaluatorsResponse = await runEvaluatorsOnLog({
        logId: log.id,
        evaluatorVersionIds: [evaluator.version_id],
      });

      const newEvaluatorLogsResponse = runEvaluatorsResponse.data;
      const newEvaluatorLogs = newEvaluatorLogsResponse.map(parseLogResponse) as EvaluatorLog[];
      onRunEvaluations(newEvaluatorLogs);
    } catch (error: any) {
      hlToastApiError({
        error,
        titleFallback: "Failed to run evaluators",
      });
    } finally {
      setRunningEvaluatorVersionIds(
        runningEvaluatorVersionIds.filter((versionId) => versionId !== evaluator.version_id),
      );
    }
  };

  return (
    <div className="relative">
      <PageSectionHeader
        title="Evaluations"
        size="sm"
        actionsTray={
          <InlineMenu>
            <ContextMenu.Item
              withinDropdownMenu
              IconLeft={CogIcon}
              href={
                getFileHref({
                  slug,
                  id: file.id,
                  page: "dashboard",
                }) + "?monitoring=true" // query param to open monitoring modal
              }
            >
              Connected Evaluators
            </ContextMenu.Item>
          </InlineMenu>
        }
      />

      {latestEvaluatorLogPerEvaluator.length > 0 ? (
        <DescriptionList className="!grid-cols-[188px_auto_auto]">
          {latestEvaluatorLogPerEvaluator.map(({ evaluator, evaluatorLog }) => {
            const isRunning = runningEvaluatorVersionIds.includes(evaluator.version_id);
            const isHumanEvaluator = evaluator.spec.evaluator_type === "human";
            const isExternalEvaluator = evaluator.spec.evaluator_type === "external";

            return (
              <React.Fragment key={evaluator.version_id}>
                <DescriptionTerm>
                  <FileLabelWithPopover file={evaluator} size={24} displayFile={true} />
                </DescriptionTerm>

                <DescriptionDetails className="">
                  <div className="flex items-center gap-8">
                    {!isRunning &&
                      (evaluatorLog ? (
                        <EvaluationValueWithPopover evaluatorLog={evaluatorLog} onFeedbackChange={onFeedbackChange} />
                      ) : (
                        <>
                          {isExternalEvaluator ? (
                            <Tooltip content={`Humanloop cannot run an external evaluator`}>
                              <IconButton disabled styling="outline" Icon={Play} size={20} />
                            </Tooltip>
                          ) : isHumanEvaluator ? (
                            <Tooltip content={`No human feedback given yet`}>
                              <div>
                                <None />
                              </div>
                            </Tooltip>
                          ) : (
                            <IconButton
                              disabled={isHumanEvaluator || isExternalEvaluator}
                              styling="outline"
                              Icon={Play}
                              onClick={() => handleRun(evaluator, log)}
                              size={20}
                            />
                          )}
                        </>
                      ))}
                    {isRunning && (
                      <div className="ml-12 flex h-20 items-center">
                        <TypingIndicator />
                      </div>
                    )}
                  </div>
                </DescriptionDetails>
                <div className="flex w-full justify-end">
                  <EvaluationDetailsActions
                    evaluator={evaluator}
                    evaluatorLog={evaluatorLog}
                    handleRun={() => handleRun(evaluator, log)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </DescriptionList>
      ) : (
        <DescriptionDetails />
      )}
    </div>
  );
};
